import { useState } from 'preact/hooks';
import { pemEncode } from '../../core/bytes/encoding';
import { discoverChain, ChainDiscoveryError } from '../../core/chain/discover';
import { SOURCE_LABEL, type ChainSource, type DiscoveredChain } from '../../core/chain/types';
import { chainFromLogs } from '../chainFromLogs';
import { hasProxy, transport } from '../state/transport';
import { isDesktop, systemRoots } from '../state/desktop';
import { Badge } from './Badge';
import { Icon } from './Icon';

/**
 * "Find the issuer chain" button. Web: whatsmychaincert.com (SSLMate; CORS-enabled), the CT log entry
 * named by an SCT, then crt.sh's CA database (needs the proxy). Desktop: the CT log entry, the CA's
 * AIA URLs (no CORS in the desktop app) and the operating system's trust store — no third-party service.
 */
export function ChainDiscovery({ chain, onChain, label = 'Find the issuer chain' }: { chain: Uint8Array[]; onChain: (pem: string, chain: DiscoveredChain) => void; label?: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>();
  const [found, setFound] = useState<DiscoveredChain>();
  const desktop = isDesktop();
  // Desktop: everything local — a CT log entry, the CA's AIA URLs, then the OS trust store for the root.
  const sources: ChainSource[] = desktop ? ['ct-log', 'aia', 'system-roots'] : ['whatsmychaincert', 'ct-log', 'crt.sh'];
  const run = async () => {
    if (chain.length === 0) return;
    setBusy(true);
    setErr(undefined);
    setFound(undefined);
    try {
      const r = await discoverChain(chain, { transport, sources, fromLogs: chainFromLogs, ...(desktop ? { systemRoots } : {}) });
      setFound(r);
      onChain(r.chain.map((d) => pemEncode(d)).join(''), r);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="stack" style="gap:0.5rem">
      <div class="row">
        <button type="button" class="btn btn-sm" onClick={run} disabled={chain.length === 0 || busy} title={`Tries: ${sources.map((s) => SOURCE_LABEL[s]).join(', then ')}`}>
          <Icon name="link" /> {busy ? 'Searching…' : label}
        </button>
        {!busy && !found && !err && (
          <span class="tiny muted">{desktop ? 'via a CT log entry, then the CA\'s AIA URLs, then the system trust store' : `via whatsmychaincert.com, then a CT log entry, then crt.sh${hasProxy.value ? '' : ' (proxy)'}`}</span>
        )}
        {found && (
          <span class="small row">
            <Badge tone="ok">{found.chain.length - chain.length} issuer{found.chain.length - chain.length === 1 ? '' : 's'} from {SOURCE_LABEL[found.source]}</Badge>
            {found.endsAtRoot ? <Badge tone="ok">ends at a self-signed root</Badge> : <Badge tone="warn">does not reach a root</Badge>}
            <span class="muted" title={found.detail}>{found.subjects.slice(chain.length).map((s) => /CN=([^,]+)/.exec(s)?.[1] ?? s).join(' → ')}</span>
          </span>
        )}
      </div>
      {err instanceof ChainDiscoveryError && (
        <div class="errorbox">
          <strong>No source could complete the chain</strong>
          <ul class="plain small" style="display:grid;gap:0.2rem">
            {err.attempts.map((a) => (
              <li key={a.source}><span class="muted">{SOURCE_LABEL[a.source]}:</span> {a.error}</li>
            ))}
            {!desktop && <li class="muted">AIA (CA servers) is only used by the desktop app — browsers cannot read those servers.</li>}
          </ul>
        </div>
      )}
      {err !== undefined && !(err instanceof ChainDiscoveryError) && <div class="errorbox">{err instanceof Error ? err.message : String(err)}</div>}
    </div>
  );
}
